import { z } from 'zod';
import { normalizedUuidSchema, UUID_PATTERN, uuidSchema } from './common';
import {
  ALLOWED_IMAGE_CONTENT_TYPES,
  ALPHA_IMAGE_CONTENT_TYPES,
  type AllowedImageContentType,
  FACE_MASK_SEGMENT,
  FACE_PHOTO_SEGMENT,
  FACE_SAFE_AREA_SEGMENT,
  FACE_SHADING_SEGMENT,
  faceMaskKeySchema,
  facePhotoKeySchema,
  facePhotoSizeSchema,
  faceSafeAreaKeySchema,
  faceShadingKeySchema,
  IMAGE_CONTENT_TYPE_EXTENSIONS,
  SAFE_AREA_IMAGE_CONTENT_TYPES,
} from './image-asset';

export {
  ALLOWED_IMAGE_CONTENT_TYPES,
  type AllowedImageContentType,
  type FacePhotoSize,
  IMAGE_CONTENT_TYPE_EXTENSIONS,
  MAX_IMAGE_UPLOAD_BYTES,
} from './image-asset';

// ---------------------------------------------------------------------------
// storage 계약 — presigned 업로드. 버킷은 비공개(assets-bucket 모듈)이며
// 브라우저는 api 가 발급한 presigned URL 로만 PUT/GET 한다.
// ---------------------------------------------------------------------------

export const CATALOG_UPLOAD_PURPOSES = [
  'PRODUCT_IMAGE',
  'PRODUCT_DETAIL',
  'COLOR_PREVIEW',
] as const;
export type CatalogUploadPurpose = (typeof CATALOG_UPLOAD_PURPOSES)[number];
/**
 * 편집영역 면 에셋 — 면 사진과 합성 에셋(마스크·음영·안내선, ADR-0007 D6·D14).
 * 모두 tmp/faces/ 임시 키에 올린 뒤 서버가 검증·확정하는 같은 흐름을 쓴다.
 */
export const FACE_ASSET_UPLOAD_PURPOSES = [
  'EDIT_FACE_PHOTO',
  'EDIT_FACE_MASK',
  'EDIT_FACE_SHADING',
  'EDIT_FACE_SAFE_AREA',
] as const;
export type FaceAssetUploadPurpose = (typeof FACE_ASSET_UPLOAD_PURPOSES)[number];
export const UPLOAD_PURPOSES = [...CATALOG_UPLOAD_PURPOSES, ...FACE_ASSET_UPLOAD_PURPOSES] as const;
export type UploadPurpose = (typeof UPLOAD_PURPOSES)[number];

/**
 * 서버가 생성하는 키 `products/{상품ID}/{세그먼트}/{uuid}.{확장자}` 의 어휘.
 * 키 생성(api storage)과 키 형식 계약(catalog assetKeySchema)이 같은 표를 본다 —
 * 한쪽만 바뀌면 방금 발급한 키가 저장에서 거부되므로 반드시 여기서만 늘린다.
 */
export const CATALOG_UPLOAD_PURPOSE_SEGMENTS = {
  PRODUCT_IMAGE: 'images',
  PRODUCT_DETAIL: 'detail',
  COLOR_PREVIEW: 'colors',
} as const satisfies Record<CatalogUploadPurpose, string>;
export const UPLOAD_PURPOSE_SEGMENTS = {
  ...CATALOG_UPLOAD_PURPOSE_SEGMENTS,
  EDIT_FACE_PHOTO: FACE_PHOTO_SEGMENT,
  EDIT_FACE_MASK: FACE_MASK_SEGMENT,
  EDIT_FACE_SHADING: FACE_SHADING_SEGMENT,
  EDIT_FACE_SAFE_AREA: FACE_SAFE_AREA_SEGMENT,
} as const satisfies Record<UploadPurpose, string>;

/** 면 에셋 용도별 허용 형식. 마스크·음영은 알파가 필요해 PNG·WebP, 안내선은 PNG 만 받는다. */
export const FACE_ASSET_CONTENT_TYPES = {
  EDIT_FACE_PHOTO: ALLOWED_IMAGE_CONTENT_TYPES,
  EDIT_FACE_MASK: ALPHA_IMAGE_CONTENT_TYPES,
  EDIT_FACE_SHADING: ALPHA_IMAGE_CONTENT_TYPES,
  EDIT_FACE_SAFE_AREA: SAFE_AREA_IMAGE_CONTENT_TYPES,
} as const satisfies Record<FaceAssetUploadPurpose, readonly AllowedImageContentType[]>;

export const PRESIGN_EXPIRES_IN_SECONDS = 300;
export const FACE_PHOTO_CONFIRM_EXPIRES_IN_SECONDS = 3600;
/**
 * 표시용 presigned GET 만료. 업로드(5분)보다 길게 둔다 — 어드민이 상세 화면을 열어둔 채 작업하는 시간을 감안.
 * EC2 역할 임시 자격증명 수명에 묶여 더 늘리지 않는다(Phase 6 D12). 만료 URL 은 화면 refetch 로 재발급된다 —
 * web 은 이 값으로 0.8×TTL 재조회 + 이미지 onError 때 invalidate 한다. 응답마다 만료를 싣지 않는다.
 */
export const VIEW_URL_EXPIRES_IN_SECONDS = 3600;

export const catalogPresignUploadInputSchema = z.object({
  productId: uuidSchema,
  purpose: z.enum(CATALOG_UPLOAD_PURPOSES),
  contentType: z.enum(ALLOWED_IMAGE_CONTENT_TYPES),
});
export type CatalogPresignUploadInput = z.infer<typeof catalogPresignUploadInputSchema>;
const faceAssetPresignUploadInputSchema = <TPurpose extends FaceAssetUploadPurpose>(
  purpose: TPurpose,
) =>
  z.strictObject({
    productId: normalizedUuidSchema,
    purpose: z.literal(purpose),
    contentType: z.enum(FACE_ASSET_CONTENT_TYPES[purpose]),
  });
export const facePhotoPresignUploadInputSchema =
  faceAssetPresignUploadInputSchema('EDIT_FACE_PHOTO');
export type FacePhotoPresignUploadInput = z.infer<typeof facePhotoPresignUploadInputSchema>;
export const faceMaskPresignUploadInputSchema = faceAssetPresignUploadInputSchema('EDIT_FACE_MASK');
export const faceShadingPresignUploadInputSchema =
  faceAssetPresignUploadInputSchema('EDIT_FACE_SHADING');
export const faceSafeAreaPresignUploadInputSchema =
  faceAssetPresignUploadInputSchema('EDIT_FACE_SAFE_AREA');
export type FaceAssetPresignUploadInput =
  | FacePhotoPresignUploadInput
  | z.infer<typeof faceMaskPresignUploadInputSchema>
  | z.infer<typeof faceShadingPresignUploadInputSchema>
  | z.infer<typeof faceSafeAreaPresignUploadInputSchema>;
export const presignUploadInputSchema = z.discriminatedUnion('purpose', [
  catalogPresignUploadInputSchema,
  facePhotoPresignUploadInputSchema,
  faceMaskPresignUploadInputSchema,
  faceShadingPresignUploadInputSchema,
  faceSafeAreaPresignUploadInputSchema,
]);
export type PresignUploadInput = z.infer<typeof presignUploadInputSchema>;

export const catalogPresignUploadResultSchema = z.object({
  uploadUrl: z.url(),
  /** 업로드 완료 후 섹션 저장 API 에 되돌려줄 S3 키. */
  key: z.string().min(1),
  expiresInSeconds: z.number().int().positive(),
  // 면 사진 응답이 legacy 결과로 우회 파싱되는 것을 막는다.
  purpose: z.never().optional(),
});
export type CatalogPresignUploadResult = z.infer<typeof catalogPresignUploadResultSchema>;
export const facePhotoPresignUploadResultSchema = z.strictObject({
  purpose: z.enum(FACE_ASSET_UPLOAD_PURPOSES),
  uploadId: normalizedUuidSchema,
  uploadUrl: z.url(),
  temporaryKey: z
    .string()
    .regex(
      new RegExp(
        `^tmp/${FACE_PHOTO_SEGMENT}/${UUID_PATTERN}\\.(${Object.values(IMAGE_CONTENT_TYPE_EXTENSIONS).join('|')})$`,
      ),
    ),
  expiresInSeconds: z.number().int().positive(),
  confirmExpiresInSeconds: z.number().int().positive(),
});
export type FacePhotoPresignUploadResult = z.infer<typeof facePhotoPresignUploadResultSchema>;
export const presignUploadResultSchema = z.union([
  facePhotoPresignUploadResultSchema,
  catalogPresignUploadResultSchema,
]);
export type PresignUploadResult = z.infer<typeof presignUploadResultSchema>;

export const confirmFacePhotoUploadInputSchema = z.strictObject({
  productId: normalizedUuidSchema,
  uploadId: normalizedUuidSchema,
  photoSize: facePhotoSizeSchema,
});
export type ConfirmFacePhotoUploadInput = z.infer<typeof confirmFacePhotoUploadInputSchema>;
/** 확정 결과와 표시 URL 갱신의 키 — 면 사진이나 합성 에셋(마스크·음영·안내선)의 최종 키다. */
export const faceAssetKeySchema = z.union([
  facePhotoKeySchema,
  faceMaskKeySchema,
  faceShadingKeySchema,
  faceSafeAreaKeySchema,
]);
export const confirmFacePhotoUploadResultSchema = z.strictObject({
  photoKey: faceAssetKeySchema,
  photoSize: facePhotoSizeSchema,
  photoUrl: z.url(),
});
export type ConfirmFacePhotoUploadResult = z.infer<typeof confirmFacePhotoUploadResultSchema>;

/**
 * 한 번의 표시 URL 갱신에 싣는 키 수 상한. 이 갱신은 query(GET)라 키가 URL 에 실리고, 링크는 전송 URL 상한
 * (trpc-transport.ts 의 TRPC_MAX_URL_LENGTH)을 넘는 단건을 보내지 않고 거부한다 — 가장 긴 키(안내선) 기준으로도
 * 한 건이 그 상한 안에 들어가는 수다(trpc-transport.spec). 상품 하나의 키는 최대 30색상 × 4면 사진 + 4면 × 합성
 * 에셋 3종(마스크·음영·안내선) = 132 개라 어드민이 이 수씩 나눠 차례로 받는다(2026-10-08, 옛 상한 132 를 낮춤).
 */
export const MAX_FACE_PHOTO_URL_KEYS = 20;
/** 면 사진과 합성 에셋을 함께 받는다. 필드 이름(photoKeys·photos)은 호환을 위해 그대로 둔다. */
export const resolveFacePhotoUrlsInputSchema = z
  .strictObject({
    productId: normalizedUuidSchema,
    photoKeys: z.array(faceAssetKeySchema).min(1).max(MAX_FACE_PHOTO_URL_KEYS),
  })
  .superRefine(({ productId, photoKeys }, ctx) => {
    photoKeys.forEach((key, index) => {
      // 세그먼트(faces·face-masks·face-shadings·face-safe-areas)는 키 형식이 고정하므로 상품 폴더만 본다.
      if (!key.startsWith(`products/${productId}/`))
        ctx.addIssue({
          code: 'custom',
          path: ['photoKeys', index],
          message: '해당 상품의 확정된 면 사진·합성 에셋을 사용해 주세요.',
        });
    });
  });
export type ResolveFacePhotoUrlsInput = z.infer<typeof resolveFacePhotoUrlsInputSchema>;
export const resolveFacePhotoUrlsResultSchema = z.strictObject({
  photos: z
    .array(z.strictObject({ photoKey: faceAssetKeySchema, photoUrl: z.url() }))
    .max(MAX_FACE_PHOTO_URL_KEYS),
});
export type ResolveFacePhotoUrlsResult = z.infer<typeof resolveFacePhotoUrlsResultSchema>;
